import type { tWapiDisconnectionCase } from 'src/alerts/WapiDisconnection.types';
import { eUserRole } from 'src/providers/UserProvider/User.enums';
import type {
  tAlertSnapshotOverride,
  tAlertSnapshotPatch,
  tAlertSnapshotRawInputs,
} from 'src/providers/AlertCenterProvider/AlertCenter.types';

/**
 * One preset per `ALERT_REGISTRY` declaration, plus an adversarial set and a
 * worst-case combo, for `AlertSimulatorPanel` (DevToolbar-only, RET-1871).
 *
 * `kind` is 'declaration' for the 1:1 registry presets (asserted complete by
 * `AlertSimulator.presets.test.ts`'s anti-rot guard), 'adversarial' for the
 * malformed-input set, and 'combo' for the deliberately-not-1:1 multi-alert
 * scenario — a superset the design's two-kind sketch didn't anticipate, kept
 * as a distinct kind rather than overloading 'adversarial' so the anti-rot
 * guard's `kind === 'declaration'` filter stays exact.
 */
export type tAlertPresetKind = 'declaration' | 'adversarial' | 'combo';

export type tAlertPreset = {
  id: string;
  label: string;
  kind: tAlertPresetKind;
  override: Omit<tAlertSnapshotOverride, 'presetId'>;
};

function patch(fields: tAlertSnapshotPatch): Omit<tAlertSnapshotOverride, 'presetId'> {
  return { version: 1, patch: fields };
}

function rawInputs(fields: tAlertSnapshotRawInputs): Omit<tAlertSnapshotOverride, 'presetId'> {
  return { version: 1, rawInputs: fields };
}

// ── Declaration presets — one per ALERT_REGISTRY id (18 today) ──────────────
// Minimal fields making that id's `derive()` true, plus its first eligible
// role and (when scoped) its first eligible surface, so the preset also
// surfaces once applied through the real `selectAlerts` role/surface gates —
// not just through `derive()` in isolation.
const DECLARATION_PRESETS: tAlertPreset[] = [
  {
    id: 'plan-pause-active',
    label: 'Plan pause active (P0)',
    kind: 'declaration',
    override: patch({
      planPauseActive: true,
      planPauseEndsAt: '18 de diciembre de 2026',
      role: eUserRole.OWNER,
    }),
  },
  {
    id: 'wapi-disconnected',
    label: 'WAPI disconnected (P0)',
    kind: 'declaration',
    // The cause is what the declaration branches on, so a preset without one
    // holds the alert back exactly as an unresolved account read does.
    override: patch({
      wapiDisconnected: true,
      wapiDisconnectCause: 'REMOVED_BY_META',
      wapiDisconnection: null,
      role: eUserRole.OWNER,
    }),
  },
  {
    id: 'ww-banned',
    label: 'WhatsApp Web banned (P0)',
    kind: 'declaration',
    override: patch({ whatsAppWebErrorCode: 'BANNED', role: eUserRole.OWNER }),
  },
  {
    id: 'account-health',
    label: 'Account health restricted (P0)',
    kind: 'declaration',
    override: patch({
      canAccessWhatsAppApi: true,
      accountHealthStatus: 'RESTRICTED',
      role: eUserRole.OWNER,
    }),
  },
  {
    id: 'quota-reached',
    label: 'Quota reached — 100% (P0)',
    kind: 'declaration',
    override: patch({
      isQuotaEligiblePlan: true,
      activeLeadsCount: 1000,
      activeLeadsLimit: 1000,
      role: eUserRole.OWNER,
    }),
  },
  {
    id: 'la-usage-capped',
    label: 'Lead Agent usage capped — 100% (P0)',
    kind: 'declaration',
    override: patch({
      leadAgentCapacityPercent: 100,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'quota-near',
    label: 'Quota near — 90% (P1)',
    kind: 'declaration',
    override: patch({
      isQuotaEligiblePlan: true,
      activeLeadsCount: 900,
      activeLeadsLimit: 1000,
      role: eUserRole.OWNER,
    }),
  },
  {
    id: 'la-usage-near',
    label: 'Lead Agent usage near — 85% (P1)',
    kind: 'declaration',
    override: patch({
      leadAgentCapacityPercent: 85,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'la-trial-cap',
    label: 'Lead Agent trial cap reached (P1)',
    kind: 'declaration',
    override: patch({
      leadAgentTrialCapReached: true,
      leadAgentTrialDaysRemaining: 2,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'la-expired',
    label: 'Lead Agent expired (P1)',
    kind: 'declaration',
    override: patch({
      leadAgentExpired: true,
      leadAgentDismissedAt: null,
      leadAgentDaysSinceExpiry: 3,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'la-shutdown',
    label: 'Lead Agent shutdown within trial (P1)',
    kind: 'declaration',
    override: patch({
      leadAgentPausedWithinTrial: true,
      leadAgentShutdownDate: '3 de septiembre',
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'calendar-revoked',
    label: 'Calendar connection revoked (P1, AGI-352)',
    kind: 'declaration',
    override: patch({
      calendarConnectionRevoked: { status: 'ready', value: true },
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    /*
     * AGI-428. El patch trae un payload COMPLETO porque la fuente lo valida en
     * runtime: un preset con la mitad de las columnas pasaria el `derive` de
     * mentira aqui y seria descartado por `parseMetaPricingNotice` en el
     * navegador, que es justo la clase de preset que "funciona" en su test y
     * no hace nada en la pantalla.
     *
     * Digital RSVP del CSV de Fanny (corte 11-sep-2026): MXN, sube extremo,
     * 1.1 -> 177.24 USD en 28 dias, que el comando convierte a 29 -> 4,693 MXN
     * al mes. Es el ejemplo con el que se reviso el prototipo.
     */
    id: 'meta-pricing-2026-10',
    label: 'Aviso precios Meta 1-oct (P1, AGI-428)',
    kind: 'declaration',
    override: patch({
      metaPricingNotice: {
        status: 'ready',
        value: {
          key: 'meta-pricing-2026-10',
          visibleFrom: '2026-09-17T15:00:00Z',
          expiresAt: '2026-10-06T05:59:59Z',
          payload: {
            version: 1,
            segment: 'sube_extremo',
            copyVariant: 'sube_extremo',
            urgency: 'alta',
            keyAccount: false,
            currency: 'MXN',
            amountToday: 29,
            amountOctober: 4693,
            usdToday28d: 1.1,
            usdOctober28d: 177.24,
            businessName: 'Digital RSVP',
            ctaUrl:
              'https://meetings.hubspot.com/ana-ramirez4/asesoramiento-personalizado?uuid=91af08ca-8151-47b4-8678-dd2b985870b5',
            videoUrl: null,
            batch: 1,
            source: 'wapi-pronostico-1oct-por-cliente-FINAL-2026-09-11',
          },
        },
      },
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    // INT-1522 (Fase 3/3 de INT-715): a Meta token went invalid. Same shape
    // as meta-pricing-2026-10 above, on the sibling notice type.
    id: 'meta-messenger-token-invalid',
    label: 'Meta token invalid (P0, INT-1522)',
    kind: 'declaration',
    override: patch({
      metaTokenInvalidNotice: {
        status: 'ready',
        value: {
          // Account-hash-namespaced key (see MetaTokenInvalidNotice.types.ts
          // -- notice_key embeds a hash of meta_account_id, not a channel).
          // The exact hash does not matter here: the front only needs the
          // prefix (isMetaTokenInvalidNoticeKey) to match.
          key: 'meta-messenger-token-invalid-a1b2c3d4e5f6a7b8',
          visibleFrom: '2026-09-25T23:09:31Z',
          expiresAt: '2026-10-25T23:09:31Z',
          payload: {
            metaAccountId: 'acc-simulated-0001',
          },
        },
      },
      role: eUserRole.OWNER,
    }),
  },
  {
    /*
     * AGI-428, mensaje 2 (25-sep): el mismo Digital RSVP con la llave del
     * mensaje 2 y un video de YouTube (el explicador de spam que ya corre en
     * el Centro de Avisos), para ver el reproductor en el detalle.
     */
    id: 'meta-pricing-2026-10-tips',
    label: 'Aviso precios Meta, mensaje 2 con video (P1, AGI-428)',
    kind: 'declaration',
    override: patch({
      metaPricingNotice: {
        status: 'ready',
        value: {
          key: 'meta-pricing-2026-10-tips',
          visibleFrom: '2026-09-25T23:00:00Z',
          expiresAt: '2026-10-06T05:59:59Z',
          payload: {
            version: 1,
            segment: 'sube_extremo',
            copyVariant: 'sube_extremo',
            urgency: 'alta',
            keyAccount: false,
            currency: 'MXN',
            amountToday: 29,
            amountOctober: 4693,
            usdToday28d: 1.1,
            usdOctober28d: 177.24,
            businessName: 'Digital RSVP',
            ctaUrl:
              'https://meetings.hubspot.com/karireyes/nuevo-pricing-wapi?uuid=f985e553-a6d9-4912-b1c7-1afef4055841',
            videoUrl: 'https://www.youtube.com/watch?v=D3ZuckNrngE',
            batch: 1,
            source: 'mensaje-2-2026-09-25',
          },
        },
      },
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    /*
     * AGI-428, mensaje 3 (6-oct): el nuevo saldo, mismo Digital RSVP con la
     * llave del mensaje 3. Fuera del simulador, la fuente solo lo deja pasar
     * si el workspace ve el saldo (`gateMetaPricingNoticeOnWallet`); el patch
     * pisa el snapshot ya filtrado, asi que aqui se ve siempre.
     */
    id: 'meta-pricing-2026-10-balance',
    label: 'Aviso precios Meta, mensaje 3 nuevo saldo (P1, AGI-428)',
    kind: 'declaration',
    override: patch({
      metaPricingNotice: {
        status: 'ready',
        value: {
          key: 'meta-pricing-2026-10-balance',
          visibleFrom: '2026-10-07T15:00:00Z',
          expiresAt: '2026-10-21T05:59:59Z',
          payload: {
            version: 1,
            segment: 'sube_extremo',
            copyVariant: 'sube_extremo',
            urgency: 'alta',
            keyAccount: false,
            currency: 'MXN',
            amountToday: 29,
            amountOctober: 4693,
            usdToday28d: 1.1,
            usdOctober28d: 177.24,
            businessName: 'Digital RSVP',
            ctaUrl:
              'https://meetings.hubspot.com/ana-ramirez4/asesoramiento-personalizado?uuid=91af08ca-8151-47b4-8678-dd2b985870b5',
            videoUrl: 'https://www.youtube.com/watch?v=D3ZuckNrngE',
            batch: 1,
            source: 'mensaje-3-2026-10-06',
          },
        },
      },
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    /*
     * RET-2191. Payload completo por la misma razon que el de Meta: la fuente
     * lo valida en runtime. Starter sin tope, que es el caso mas comun del
     * cohorte; el caso "volvio a responder" (`wasCapped: true`) se ve en el
     * harness de avisos del Lead Agent.
     */
    id: 'lead-agent-cap-raise-2026-10',
    label: 'Subida de caps del Lead Agent (P3, RET-2191)',
    kind: 'declaration',
    override: patch({
      leadAgentCapRaiseNotice: {
        status: 'ready',
        value: {
          key: 'lead-agent-cap-raise-2026-10',
          visibleFrom: '2026-10-01T15:00:00Z',
          expiresAt: '2026-10-31T06:00:00Z',
          payload: { tier: 'STARTER', wasCapped: false },
        },
      },
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'la-transfer-pending',
    label: 'Lead Agent transfer pending payment (P1)',
    kind: 'declaration',
    override: patch({
      pendingPayment: { status: 'ready', value: true },
      leadAgentInvoiceAmount: '$1 250 MXN',
      leadAgentInvoiceDueDate: '28 de agosto',
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'la-acceptance',
    label: 'Lead Agent acceptance pending (P1)',
    kind: 'declaration',
    override: patch({
      leadAgentAcceptancePending: true,
      leadAgentTier: 'pro',
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'user-inactive',
    label: 'User inactive (P1)',
    kind: 'declaration',
    override: patch({ userInactive: true, role: eUserRole.OWNER }),
  },
  {
    id: 'wapi-not-connected',
    label: 'WAPI never connected (P1)',
    kind: 'declaration',
    // `wapiDisconnected` stays false on purpose: both share the `wapi`
    // exclusivity group, so a preset that set it would apply and then be
    // swallowed by the P0 it was meant to sit beside.
    override: patch({ wapiNoAccount: true, role: eUserRole.OWNER }),
  },
  {
    id: 'wapi-activation-failed',
    label: 'WAPI activation failed, reconnect (P1)',
    kind: 'declaration',
    // INT-1516. Alone in the `wapi` group for the same reason as the two
    // presets around it: a lit sibling would take the group.
    override: patch({ wapiActivationFailed: true, role: eUserRole.OWNER }),
  },
  {
    id: 'wapi-activation-pending',
    label: 'WAPI held, Meta confirming the number (P3)',
    kind: 'declaration',
    // INT-1516. The lowest tier of the `wapi` group: any sibling would win.
    override: patch({ wapiActivationPending: true, role: eUserRole.OWNER }),
  },
  {
    id: 'routing-conflict',
    label: 'WAPI inbound routing conflict (P1)',
    kind: 'declaration',
    // Same reason the preset above leaves `wapiDisconnected` alone: all three
    // share the `wapi` exclusivity group, so a preset that lit a sibling would
    // apply and then be swallowed by the higher tier it was meant to sit
    // beside.
    override: patch({ wapiRoutingConflict: true, role: eUserRole.OWNER }),
  },
  {
    id: 'la-seller-paused',
    label: 'Lead Agent seller paused (P2)',
    kind: 'declaration',
    override: patch({
      sellerPaused: { status: 'ready', value: true },
      ownerName: 'CEROMULTAS M',
      role: eUserRole.SELLER,
    }),
  },
  {
    id: 'la-trial-activation',
    label: 'Lead Agent trial activatable (P3)',
    kind: 'declaration',
    override: patch({
      leadAgentTrialActivatable: true,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'billing-migration',
    label: 'Billing migration in progress (P3)',
    kind: 'declaration',
    override: patch({
      isBillingMigrationInProgress: true,
      role: eUserRole.OWNER,
      surface: 'funnels',
    }),
  },
  {
    id: 'stage-threshold-explainer',
    label: 'First red card on the board (P3)',
    kind: 'declaration',
    // U9 — the board normally publishes these through
    // `reportBoardExpiredLead`; the simulator patches the resulting snapshot
    // fields directly so the explainer can be inspected without waiting for a
    // stage threshold to actually elapse.
    override: patch({
      boardHasExpiredLead: true,
      boardExpiredStageId: 'stage-1',
      boardExpiredStageName: 'Prospectos',
      boardExpiredLeadName: 'Mariana Robles',
      boardExpiredElapsed: '10 minutos',
      role: eUserRole.OWNER,
      surface: 'funnels',
    }),
  },
  {
    /*
     * U12 (RET-1269) — day 3: the quiet yellow rung in the bell, which is where
     * 76% of recoveries happen. Every debt field is populated so the reason and
     * retry lines both render; the adversarial combos below strip them one at a
     * time.
     */
    id: 'past-due-early',
    label: 'Past due — day 3, soft/yellow in the bell (P1)',
    kind: 'declaration',
    override: patch({
      subscriptionPastDue: true,
      daysPastDue: 3,
      pastDueAmount: '$1 250 MXN',
      pastDueCancellationDate: '30 de septiembre de 2026',
      pastDueCancellationPassed: false,
      pastDueInvoiceUrl: 'https://invoice.stripe.com/i/acct_x/test_y',
      pastDueNextPaymentAttempt: '14 de septiembre de 2026',
      pastDueAttempted: true,
      pastDueAttemptCount: 2,
      pastDueCollectionMethod: 'charge_automatically',
      pastDueDeclineCode: 'insufficient_funds',
      pastDueCardBrand: 'Visa',
      pastDueCardLast4: '4242',
      role: eUserRole.OWNER,
    }),
  },
  {
    id: 'past-due-final',
    label: 'Past due — day 11, the wide countdown strip (P0)',
    kind: 'declaration',
    override: patch({
      subscriptionPastDue: true,
      daysPastDue: 11,
      pastDueAmount: '$1 250 MXN',
      pastDueCancellationDate: '30 de septiembre de 2026',
      pastDueCancellationPassed: false,
      pastDueInvoiceUrl: 'https://invoice.stripe.com/i/acct_x/test_y',
      pastDueNextPaymentAttempt: null,
      pastDueAttempted: true,
      pastDueAttemptCount: 4,
      pastDueCollectionMethod: 'charge_automatically',
      pastDueDeclineCode: 'insufficient_funds',
      pastDueCardBrand: 'Visa',
      pastDueCardLast4: '4242',
      role: eUserRole.OWNER,
    }),
  },
  {
    /*
     * U13 — the same day 3, seen by someone who cannot pay. No amount, no card,
     * no reason, no CTA: what a seller gets is the fact and, from day 5, the
     * date.
     */
    id: 'past-due-seller',
    label: 'Past due (seller) — day 3, one quiet fact (P3)',
    kind: 'declaration',
    override: patch({
      subscriptionPastDue: true,
      daysPastDue: 3,
      pastDueCancellationDate: '30 de septiembre de 2026',
      pastDueCancellationPassed: false,
      pastDueAttempted: true,
      pastDueAttemptCount: 2,
      role: eUserRole.SELLER,
    }),
  },
  {
    id: 'past-due-seller-final',
    label: 'Past due (seller) — day 11, the whole team loses access (P0)',
    kind: 'declaration',
    override: patch({
      subscriptionPastDue: true,
      daysPastDue: 11,
      pastDueCancellationDate: '30 de septiembre de 2026',
      pastDueCancellationPassed: false,
      pastDueAttempted: true,
      pastDueAttemptCount: 4,
      role: eUserRole.SELLER,
    }),
  },
  {
    id: 'subscription-promo',
    label: 'No active plan — subscription promo (P3)',
    kind: 'declaration',
    override: patch({
      hasActivePlan: false,
      subscriptionPromoTotal: '199',
      subscriptionPromoCurrency: 'MXN',
      role: eUserRole.OWNER,
    }),
  },
];

// ── Adversarial presets — malformed / edge-case inputs ───────────────────────
const ADVERSARIAL_PRESETS: tAlertPreset[] = [
  {
    id: 'adversarial:malformed-end-date',
    label: 'Malformed subscription end date',
    kind: 'adversarial',
    override: rawInputs({ subscriptionEndDate: 'not-a-date' }),
  },
  {
    id: 'adversarial:null-billing-period',
    label: 'Null billing period id',
    kind: 'adversarial',
    override: patch({ billingPeriodId: null }),
  },
  {
    id: 'adversarial:source-loading',
    label: 'Seller-paused source stuck loading',
    kind: 'adversarial',
    override: patch({ sellerPaused: { status: 'loading', value: null } }),
  },
  {
    id: 'adversarial:source-error',
    label: 'Seller-paused source errored',
    kind: 'adversarial',
    override: patch({ sellerPaused: { status: 'error', value: null } }),
  },
  {
    // RET-1957 — the SAME declaration past its prominent phase: the family
    // hands the page back and reappears in the bell. Kind 'combo' rather than
    // 'declaration' because the anti-rot guard wants exactly one declaration
    // preset per registry id, and `la-expired` already has it.
    id: 'combo:la-expired-decayed',
    label: 'Lead Agent expired, decayed to the bell (P1, day 10)',
    kind: 'combo',
    override: patch({
      leadAgentExpired: true,
      leadAgentDismissedAt: null,
      leadAgentDaysSinceExpiry: 10,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    /*
     * U12 — the day 5-8 rung: the SAME declaration, on the page, in orange,
     * with a session-scoped close. Kind 'combo' because the anti-rot guard
     * wants exactly one declaration preset per id and `past-due-early` has it.
     */
    id: 'combo:past-due-warn',
    label: 'Past due — day 6, warn/orange on the page (P1, closable for the session)',
    kind: 'combo',
    override: patch({
      subscriptionPastDue: true,
      daysPastDue: 6,
      pastDueAmount: '$1 250 MXN',
      pastDueCancellationDate: '30 de septiembre de 2026',
      pastDueInvoiceUrl: 'https://invoice.stripe.com/i/acct_x/test_y',
      pastDueNextPaymentAttempt: '16 de septiembre de 2026',
      pastDueAttempted: true,
      pastDueAttemptCount: 3,
      pastDueDeclineCode: 'expired_card',
      pastDueCardBrand: 'Mastercard',
      pastDueCardLast4: '0000',
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    /*
     * KTD1 — the bound has GONE BY and the subscription is still PAST_DUE (day
     * 20 and day 60 both exist in U4's dataset). The rung must drop the date
     * entirely rather than recite one that has passed.
     */
    id: 'adversarial:past-due-elapsed-bound',
    label: 'Past due — day 20, cancellation bound already elapsed',
    kind: 'adversarial',
    override: patch({
      subscriptionPastDue: true,
      daysPastDue: 20,
      pastDueAmount: '$1 250 MXN',
      pastDueCancellationDate: '28 de agosto de 2026',
      pastDueCancellationPassed: true,
      pastDueAttempted: true,
      pastDueNextPaymentAttempt: null,
      role: eUserRole.OWNER,
    }),
  },
  {
    /*
     * R7 — the manual-collection cohort: no card was ever charged, so there is
     * no decline code, no retry schedule and no cancellation date at all.
     */
    id: 'adversarial:past-due-manual-collection',
    label: 'Past due — manual collection, no bound and no retries',
    kind: 'adversarial',
    override: patch({
      subscriptionPastDue: true,
      daysPastDue: 11,
      pastDueAmount: '$1 250 MXN',
      pastDueCancellationDate: null,
      pastDueInvoiceUrl: null,
      pastDueNextPaymentAttempt: null,
      pastDueAttempted: false,
      pastDueAttemptCount: null,
      pastDueCollectionMethod: 'send_invoice',
      role: eUserRole.OWNER,
    }),
  },
  {
    id: 'adversarial:numeric-plan-name',
    label: 'Numeric plan name (type-hostile)',
    kind: 'adversarial',
    override: patch({ planName: 42 }),
  },
  {
    id: 'adversarial:exclusivity-la-state',
    label: 'la-state group — 2+ members eligible at once',
    kind: 'adversarial',
    override: patch({
      leadAgentExpired: true,
      leadAgentDismissedAt: null,
      leadAgentDaysSinceExpiry: 3,
      leadAgentPausedWithinTrial: true,
      leadAgentShutdownDate: '3 de septiembre',
      sellerPaused: { status: 'ready', value: true },
      ownerName: 'CEROMULTAS M',
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    id: 'adversarial:exclusivity-la-trial',
    label: 'la-trial group — 2+ members eligible at once',
    kind: 'adversarial',
    override: patch({
      leadAgentTrialCapReached: true,
      leadAgentTrialDaysRemaining: 2,
      leadAgentTrialActivatable: true,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
];

// ── Worst-case combo — the "Cero Multas" multi-alert workspace shape ────────
const COMBO_PRESETS: tAlertPreset[] = [
  {
    id: 'combo:multi-alert-workspace',
    label: 'Multi-alert workspace (quota-near + seller-paused + WAPI disconnected)',
    kind: 'combo',
    override: patch({
      isQuotaEligiblePlan: true,
      activeLeadsCount: 900,
      activeLeadsLimit: 1000,
      sellerPaused: { status: 'ready', value: true },
      ownerName: 'CEROMULTAS M',
      wapiDisconnected: true,
      wapiDisconnectCause: 'REMOVED_BY_META',
      wapiDisconnection: null,
      role: eUserRole.SELLER,
    }),
  },
  {
    // AGI-885 — one primary per screen: a critical whose card carries a
    // button (case 0, reconnect) over the vencido banner on the page. The
    // card keeps the primary; the page's CTA steps down to tertiary.
    id: 'combo:critical-over-in-page',
    label: 'WAPI disconnected (P0, with a button) over Lead Agent expired on the page (P1)',
    kind: 'combo',
    override: patch({
      wapiDisconnected: true,
      wapiDisconnectCause: 'REMOVED_BY_META',
      wapiDisconnection: null,
      leadAgentExpired: true,
      leadAgentDismissedAt: null,
      leadAgentDaysSinceExpiry: 3,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    // AGI-885 — the same screen under a critical with no button (case 8a,
    // Meta is moving the number): the page keeps its primary.
    id: 'combo:critical-without-button-over-in-page',
    label: 'WAPI reconnecting (P0, no button) over Lead Agent expired on the page (P1)',
    kind: 'combo',
    override: patch({
      wapiDisconnection: {
        case: '8a',
        metaReason: null,
        metaInitiatedBy: null,
        occurrence: null,
      },
      leadAgentExpired: true,
      leadAgentDismissedAt: null,
      leadAgentDaysSinceExpiry: 3,
      role: eUserRole.OWNER,
      surface: 'home',
    }),
  },
  {
    // RET-1876 — the master-rule worst case: every P0 predicate true at once
    // for an owner. Exactly ONE wide strip may render (wapi-disconnected,
    // rank 0); the other four are the bell's waiting list. This preset is
    // also the fixture `AlertCenter.master-rule.test.tsx` arbitrates, so the
    // QA scenario and the pinned invariant can never drift apart.
    id: 'combo:all-criticals',
    label: 'Cero Multas — all 5 criticals at once (wide arbitration)',
    kind: 'combo',
    override: patch({
      wapiDisconnected: true,
      wapiDisconnectCause: 'REMOVED_BY_META',
      wapiDisconnection: null,
      whatsAppWebErrorCode: 'BANNED',
      canAccessWhatsAppApi: true,
      accountHealthStatus: 'RESTRICTED',
      isQuotaEligiblePlan: true,
      activeLeadsCount: 1000,
      activeLeadsLimit: 1000,
      leadAgentCapacityPercent: 100,
      // Resolved-false so the transfer alert neither fires nor suppresses
      // `la-usage-capped` (RET-1764) — the point is five LIVE criticals.
      pendingPayment: { status: 'ready', value: false },
      role: eUserRole.OWNER,
    }),
  },
];

// ── INT-1653 — one preset per WhatsApp disconnection notice ────────────────
// The owner sees every case; the seller presets cover each of their notes.
// Cases 8a to 8c fire from a LIVE row (`wapiDisconnected` stays false), the
// rest from a disconnected one, exactly as the provider resolves them.
const WAPI_CASE_LABELS: Record<tWapiDisconnectionCase, string> = {
  '0': 'sin causa',
  '1': 'USER_RE_REGISTERED',
  '2': 'CHANGE_NUMBER',
  '3': 'BUSINESS_DOWNGRADE',
  '4': 'COMPANION_INACTIVITY',
  '5': 'PRIMARY_INACTIVITY',
  '6': 'ACCOUNT_DISCONNECTED + USER',
  '7': 'ACCOUNT_DISCONNECTED + SYSTEM',
  '8a': 'reconectando',
  '8b': 'sigue desconectado',
  '8d': 'más de 24 h sin conectarse',
  '8c_same': 'reconectado, misma cuenta',
  '8c_other': 'reconectado, otra cuenta',
  '9': 'Cloud API sin permisos',
  '10': 'app desinstalada',
};

const WAPI_CASE_META: Partial<
  Record<tWapiDisconnectionCase, [string, string | null]>
> = {
  '1': ['USER_RE_REGISTERED', null],
  '2': ['CHANGE_NUMBER', null],
  '3': ['BUSINESS_DOWNGRADE', null],
  '4': ['COMPANION_INACTIVITY', 'SYSTEM'],
  '5': ['PRIMARY_INACTIVITY', 'SYSTEM'],
  '6': ['ACCOUNT_DISCONNECTED', 'USER'],
  '7': ['ACCOUNT_DISCONNECTED', 'SYSTEM'],
  '10': ['PARTNER_APP_UNINSTALLED', null],
};

function wapiCasePreset(
  disconnectionCase: tWapiDisconnectionCase,
  role: eUserRole,
): tAlertPreset {
  const isLive = ['8a', '8b', '8d', '8c_same', '8c_other'].includes(
    disconnectionCase,
  );
  const [metaReason, metaInitiatedBy] = WAPI_CASE_META[disconnectionCase] ?? [
    null,
    null,
  ];
  const roleLabel = role === eUserRole.SELLER ? 'vendedor' : 'propietario';
  return {
    id: `combo:wapi-case-${disconnectionCase}-${role.toLowerCase()}`,
    label: `WAPI ${disconnectionCase} · ${WAPI_CASE_LABELS[disconnectionCase]} (${roleLabel})`,
    kind: 'combo',
    override: patch({
      wapiDisconnected: !isLive,
      wapiDisconnectCause: isLive ? null : 'REMOVED_BY_META',
      wapiDisconnection: {
        case: disconnectionCase,
        metaReason,
        metaInitiatedBy,
        occurrence: disconnectionCase.startsWith('8c')
          ? '2026-10-19T15:10:00Z'
          : null,
      },
      role,
    }),
  };
}

const WAPI_CASE_PRESETS: tAlertPreset[] = [
  ...(Object.keys(WAPI_CASE_LABELS) as tWapiDisconnectionCase[]).map((c) =>
    wapiCasePreset(c, eUserRole.OWNER),
  ),
  ...(['0', '7', '8a', '8c_same', '8c_other'] as const).map((c) =>
    wapiCasePreset(c, eUserRole.SELLER),
  ),
];

export const ALERT_PRESETS: tAlertPreset[] = [
  ...DECLARATION_PRESETS,
  ...ADVERSARIAL_PRESETS,
  ...COMBO_PRESETS,
  ...WAPI_CASE_PRESETS,
];
